import { useRef, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';
import { useNavigate, useParams } from 'react-router-dom';

import saveAs from 'file-saver';
import { FormikProps } from 'formik';

import { Grid2 as Grid, LinearProgress } from '@mui/material';

import { Board } from '@/models/BoardModel.ts';
import { Card } from '@/models/card/CardModel.ts';
import { Comment } from '@/models/CommentModel.ts';

import Error from '../../common/Error';
import PageWrapper from '../../common/PageWrapper';
import {
  calculateDisabledFieldsForMilestone,
  getCardMetaInfo,
  getRequiredFields,
  getUpdatedFieldsAndValues,
  hasResponsibleField,
  isFieldChanged,
  populateInitialValues,
} from '../../utils/FieldUtil';
import { selectActiveBoard, selectRoomMembers } from '../../ducks/Boards';
import {
  selectMilestone,
  selectAttachments,
  selectComments,
  selectLog,
  selectIsUploadingAttachments,
  selectIsDeletingAttachments,
  selectPossibleResponsible,
  selectIsLoadingPossibleResponsible,
  selectPossibleResponsibleRequested,
  actions as milestonesActions,
} from '../../ducks/Milestones';
import { downloadMilestoneAttachment } from '../../services/Milestones';
import { alertAction } from '../../thunks/Alerts';
import * as boardsActions from '../../thunks/Boards';
import * as milestoneActions from '../../thunks/Milestones';
import { LIST_MILESTONE_VIEW_MODE, withViewModeQuery } from '../../utils/ViewModeUtil';

import MilestoneAttachments from '../common/Attachments';
import MilestoneComments from '../common/Comments';
import Form from '../common/Form';
import Log from '../common/Log';
import DeleteMilestoneDialog from '../../common/DeleteCardItemDialog';
import DeleteIconButton from '../../common/DeleteIconButton';

const mapStateToProps = (state: any, ownProp: any) =>
  createStructuredSelector({
    board: selectActiveBoard(),
    milestone: selectMilestone({ milestoneId: ownProp.params.milestoneId }),
    attachments: selectAttachments(),
    log: selectLog(),
    isUploadingAttachments: selectIsUploadingAttachments(),
    isDeletingAttachments: selectIsDeletingAttachments(),
    comments: selectComments(),
    roomMembers: selectRoomMembers(),
    possibleResponsible: selectPossibleResponsible(),
    isLoadingPossibleResponsible: selectIsLoadingPossibleResponsible(),
    possibleResponsibleRequested: selectPossibleResponsibleRequested(),
  });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators({ ...milestoneActions, ...milestonesActions, ...boardsActions }, dispatch),
});

const MilestoneUpdateContainer = ({
  board,
  milestone,
  log,
  attachments,
  isUploadingAttachments,
  isDeletingAttachments,
  comments,
  roomMembers,
  possibleResponsible,
  isLoadingPossibleResponsible,
  possibleResponsibleRequested,
  actions,
}: {
  board: Board;
  milestone: any;
  log: any;
  attachments: any;
  isUploadingAttachments: boolean;
  isDeletingAttachments: boolean;
  comments: Comment[];
  roomMembers: any;
  possibleResponsible: any;
  isLoadingPossibleResponsible: boolean;
  possibleResponsibleRequested: { boardId: string };
  actions: any;
}) => {
  const { communityId, roomId, boardId, milestoneFolderId, milestoneId } = useParams();
  const navigate = useNavigate();
  const formRef = useRef<FormikProps<any>>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [closeOnSuccess, setCloseOnSuccess] = useState(false);
  const milestoneConfig = board.milestones_config.find((config) => config.id === milestoneFolderId);
  const hasResponsible = !!hasResponsibleField({ fieldConfig: milestoneConfig });

  useEffect(() => {
    actions
      .fetchComments({ communityId, roomId, boardId, milestoneId })
      .then((action: Action) => alertAction({ action, error: intl.get('common.comments.fetch.error') }));
    actions
      .fetchAttachments({ communityId, roomId, boardId, milestoneId })
      .then((action: Action) => alertAction({ action, error: intl.get('common.attachments.fetch.error') }));
    actions
      .fetchLog({ communityId, roomId, boardId, cardId: milestoneId })
      .then((action: Action) => alertAction({ action, error: intl.get('common.log.fetch.error') }));

    if (hasResponsible && !isLoadingPossibleResponsible && possibleResponsibleRequested.boardId != boardId) {
      actions.fetchPossibleResponsible({ communityId, roomId, boardId });
    }
  }, [communityId, roomId, boardId, milestoneId, actions]);

  const handleSubmit = ({ values, item }: { values: any; item: any }) => {
    const data = {
      type: 'entry',
      fields: getUpdatedFieldsAndValues({
        values,
        item,
        fieldsConfig: milestoneConfig?.fields,
        fieldChangePredicate: isFieldChanged,
      }),
    };

    return actions
      .updateMilestone({ communityId, roomId, boardId, milestoneSet: milestoneFolderId, milestoneId: item.id, data })
      .then((action: any) =>
        alertAction({
          action,
          error: intl.get('milestone.update.error'),
          success: intl.get('milestone.update.success'),
          onSuccess: () =>
            navigate(
              withViewModeQuery(
                closeOnSuccess
                  ? `/${communityId}/${roomId}/boards/${boardId}`
                  : `/${communityId}/${roomId}/boards/${boardId}/milestoneset/${action.payload.milestoneSet}/milestones/${action.payload.milestone.id}`,
              ),
            ),
        }),
      );
  };

  const handleCancel = () => navigate(withViewModeQuery(`/${communityId}/${roomId}/boards/${boardId}`));

  const handleSubmitComment = ({ content }: { content: any }) =>
    actions.createComment({ communityId, roomId, boardId, milestoneId, content }).then((action: Action) => {
      alertAction({
        action,
        error: intl.get('common.comments.add.error'),
        success: intl.get('common.comments.add.success'),
      });
    });

  const handleUpdateComment = ({ comment }: { comment: Comment }) =>
    actions
      .updateComment({ communityId, roomId, boardId, milestoneId, commentId: comment.id, comment })
      .then((action: Action) =>
        alertAction({
          action,
          error: intl.get('common.comments.update.error'),
          success: intl.get('common.comments.update.success'),
        }),
      );

  const handleDeleteComment = ({ comment }: { comment: Comment }) =>
    actions.deleteComment({ communityId, roomId, boardId, milestoneId, commentId: comment.id }).then((action: Action) =>
      alertAction({
        action,
        error: intl.get('common.comments.delete.error'),
        success: intl.get('common.comments.delete.success'),
      }),
    );

  const handleUploadAttachment = ({ files }: { files: File[] }) => {
    if (files.length > 0) {
      actions.uploadAttachment({ communityId, roomId, boardId, milestoneId, files }).then((action: Action) =>
        alertAction({
          action,
          success: intl.get('common.attachments.upload.success'),
          error: intl.get('common.attachments.upload.error'),
        }),
      );
    }
  };

  const handleAddLinks = (links: any[]) =>
    links.forEach((link) =>
      actions.addMilestoneLink({ communityId, roomId, boardId, milestoneId, link }).then((action: Action) =>
        alertAction({
          action,
          error: intl.get('common.link.add.error'),
          success: intl.get('common.link.add.success'),
        }),
      ),
    );

  const handleDownloadAttachment = ({ attachment }: { attachment: any }) => {
    let attachmentId = attachment.id;
    let version;
    if (attachment.type === 'internal-link') {
      attachmentId = attachment.destination;
      version = attachment.destination_version;
    }
    downloadMilestoneAttachment({ communityId, roomId, boardId, milestoneId, attachmentId, version }).then(
      (response) => {
        saveAs(response.data, attachment.name);
      },
    );
  };

  const handleDeleteAttachment = ({ attachmentId }: { attachmentId: string }) =>
    actions.deleteAttachment({ communityId, roomId, boardId, milestoneId, attachmentId }).then((action: Action) =>
      alertAction({
        action,
        error: intl.get('common.attachments.delete.error'),
        success: intl.get('common.attachments.delete.success'),
      }),
    );

  const handleCloseDelete = () => {
    setOpenDeleteDialog(false);
  };

  const handleDelete = () => {
    actions
      .deleteMilestone({ communityId, roomId, boardId, milestone, milestoneSet: milestoneFolderId })
      .then((action: Action) =>
        alertAction({
          action,
          success: intl.get('board.tooltips.milestone.delete.success.message'),
          error: intl.get('board.tooltips.milestone.delete.error.message'),
          onSuccess: () => {},
        }),
      );

    navigate(`/${communityId}/${roomId}/boards/${board.id}?view=${LIST_MILESTONE_VIEW_MODE}`);
  };

  const handleSaveClick = (closeForm: boolean) => {
    const form = formRef.current;
    setCloseOnSuccess(closeForm);
    if (form) {
      form.handleSubmit();
    }
  };

  const deleteIconButton = (milestone: Card) => (
    <DeleteIconButton
      tooltipText={intl.get('board.tooltips.milestone.delete.question.title')}
      onClick={() => setOpenDeleteDialog(true)}
      disabled={!milestone.permissions.UPDATE}
    />
  );

  if (hasResponsible && (isLoadingPossibleResponsible || possibleResponsibleRequested.boardId != boardId)) {
    return <LinearProgress />;
  }

  if (!milestone || !milestoneConfig) {
    return <Error text={intl.get('milestone.not_found')} />;
  }

  return (
    <PageWrapper title={intl.get('app_bar.milestone_card')}>
      <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 8, xs: 12 }}>
          <Form
            formRef={formRef}
            item={milestone}
            initialValues={populateInitialValues({ item: milestone, fields: milestoneConfig.fields })}
            disabledFields={calculateDisabledFieldsForMilestone({
              permissions: milestone.permissions,
              fields: milestoneConfig.fields,
            })}
            requiredFields={getRequiredFields({ item: milestone, fields: milestoneConfig.fields })}
            type={board.type}
            fieldConfig={milestoneConfig}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            deleteIcon={deleteIconButton(milestone)}
            restrictions={{
              'task-done': () => ({}),
              'task-responsible': () => ({}),
              'rich-text': () => ({}),
              'auto-number': () => ({}),
              'unique-document-id': () => ({}),
              'sequence-number': () => ({}),
              member: () => ({}),
              list: () => ({}),
              date: (fieldConfig, field) => {
                if (fieldConfig.met_date_field === field.id) {
                  return { disableFuture: true };
                }
              },
              string: () => ({}),
              numeric: () => ({}),
            }}
            metaInfo={getCardMetaInfo(milestone, roomMembers)}
            possibleResponsible={possibleResponsible}
            submitOptions={[
              {
                title: intl.get('common.form.save_and_close'),
                default: true,
                handleClick: () => handleSaveClick(true),
              },
              {
                title: intl.get('common.form.save'),
                handleClick: () => handleSaveClick(false),
              },
            ]}
          />
          <DeleteMilestoneDialog
            open={openDeleteDialog}
            onClose={handleCloseDelete}
            onDelete={handleDelete}
            titles={{
              deleteDialogTitle: intl.get('board.tooltips.milestone.delete.question.title'),
              deleteDialogMessage: intl.get('board.tooltips.milestone.delete.question.message'),
            }}
          />
          <MilestoneAttachments
            activeCommunity={communityId}
            activeRoom={roomId}
            item={milestone}
            attachments={attachments}
            members={roomMembers}
            isUploading={isUploadingAttachments}
            isDeleting={isDeletingAttachments}
            onUploadAttachment={handleUploadAttachment}
            onDeleteAttachment={handleDeleteAttachment}
            onDownloadAttachment={handleDownloadAttachment}
            onAddLinks={handleAddLinks}
          />
          <Log log={log} members={roomMembers} item={milestone} />
        </Grid>
        <Grid size={{ sm: 12, md: 4, xs: 12 }}>
          <MilestoneComments
            board={board}
            item={milestone}
            comments={comments}
            onSubmitComment={handleSubmitComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            members={roomMembers}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

const ConnectedMilestoneUpdateContainer = connect(mapStateToProps, mapDispatchToProps)(MilestoneUpdateContainer);
export default ConnectedMilestoneUpdateContainer;
